import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Field } from 'formik';
import { useCookies } from 'react-cookie';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import _ from 'lodash';

import { updateModelMetadata } from 'store/actions/userData';
import {
  FovInput,
  FovSelect,
  FovOption,
  FovFieldError,
  FovButton,
  Tags,
  IndeterminateProgress,
} from 'components';
import { TagsContainer } from 'pages/ModelCanvas/SideMenus/Publish/publishStyles';

import {
  TabsCard,
  TabsRow,
  TabButton,
  TabPanel,
  OverviewCard,
  InfoTitle,
  OverviewMetaGrid,
  OverviewMetaItem,
  OverviewDivider,
  OverviewTagsSection,
  SectionEyebrow,
  TagsWrap,
  Tag,
  EmptyState,
  PanelGrid,
  InfoCard,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  FormatTag,
  PreviewBody,
  PreviewLabelText,
  ActionButton,
  ActionsInline,
  OverviewHeader,
  OverviewHeaderTitle,
  OverviewHeaderAction,
} from './styles';
import { formatNumber } from './utils';

const schema = yup.object().shape({
  contentName: yup.string().required('Name is required.'),
  categoryName: yup.string().required('Category is required.'),
  tags: yup.string().required('At least one tag is required.'),
});

const PreviewTabsCard = ({
  selectedModel,
  metadataRows = [],
  tags = [],
  normalizedStats = [],
  availableFormats = [],
  onDownload,
  onMyFiles = false,
  onMetadataSaved,
}) => {
  const [ activeTab, setActiveTab ] = useState('overview');
  const [ isEditing, setIsEditing ] = useState(false);
  const [ cookies ] = useCookies([ 'userAuth' ]);
  const theme = useTheme();
  const formikRef = useRef(null);
  const dispatch = useDispatch();

  const canvasStore = useSelector(state => state.canvas);
  const userDataStore = useSelector(state => state.userData);
  const rpcStore = useSelector(state => state.rpc);

  const token = cookies.archvision_token;
  const userId = userDataStore?.data?.userId;
  const modelsLoading = userDataStore.modelsLoading;
  const serializedData = canvasStore.serializedData;
  const statistics = serializedData?.metadata?.statistics;
  const rpcCategories = rpcStore.rpcCategories;

  const {
    category,
    subcategory,
    title: fileName,
    tags: modelTags,
    rpc_guid,
  } = selectedModel || {};

  const formCategory = subcategory || category;
  const formTags = useMemo(() => {
    return !_.isEmpty(modelTags) ? modelTags.join(',') : '';
  }, [ modelTags ]);

  const defaults = useMemo(() => ({
    contentName: fileName || '',
    categoryName: formCategory || 'Other',
    tags: formTags,
  }), [ fileName, formCategory, formTags ]);

  useEffect(() => {
    if (activeTab !== 'overview') {
      setIsEditing(false);
    }
  }, [ activeTab ]);

  useEffect(() => {
    if (!onMyFiles) {
      setIsEditing(false);
    }
  }, [ onMyFiles ]);

  useEffect(() => {
    const form = formikRef.current;

    form?.setFieldValue('contentName', fileName || '');
    form?.setFieldValue('categoryName', formCategory || 'Other');
    form?.setFieldValue('tags', formTags);

    return () => form?.resetForm();
  }, [ fileName, formCategory, formTags, selectedModel ]);

  const categories = useMemo(() => {
    if (!_.isEmpty(rpcCategories)) {
      return rpcCategories.map(categoryItem => {
        const hasChildren = categoryItem.children?.length > 0;
        const childrenArray = hasChildren
          ? [ ...categoryItem.children, categoryItem ]
          : [ categoryItem ];
        const arrayWithNested = hasChildren ? childrenArray.reverse() : childrenArray;

        return arrayWithNested.map((item, index) => {
          const { title, value, children } = item;

          return (
            <FovOption
              key={`${value}-${index}`}
              value={value}
              indent={!children}
              groupParent={children}
            >
              {title}
            </FovOption>
          );
        });
      });
    }

    return (
      <FovOption value='No categories found.'>
        No categories found.
      </FovOption>
    );
  }, [ rpcCategories ]);

  const setRef = form => {
    if (!formikRef.current) {
      formikRef.current = form;
    }
  };

  const handleSubmit = (values, setSubmitting) => {
    const payload = {
      title: values.contentName,
      tags: values.tags
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
      category: values.categoryName,
    };

    dispatch(updateModelMetadata({
      token,
      guid: rpc_guid,
      payload,
      userId,
      onComplete: () => {
        setSubmitting(false);
        setIsEditing(false);
        onMetadataSaved?.();
      },
    }));
  };

  const canEditOverview = onMyFiles && activeTab === 'overview';

  return (
    <TabsCard>
      <TabsRow>
        <TabButton
          type="button"
          $active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === 'metadata'}
          onClick={() => setActiveTab('metadata')}
        >
          Model Statistics
        </TabButton>
      </TabsRow>

      {activeTab === 'overview' && (
        <TabPanel>
          <OverviewCard>
            {!isEditing ? (
              <>
                <OverviewHeader>
                  <OverviewHeaderTitle>Metadata</OverviewHeaderTitle>

                  {canEditOverview && (
                    <OverviewHeaderAction
                      type="button"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit metadata
                    </OverviewHeaderAction>
                  )}
                </OverviewHeader>

                <OverviewMetaGrid>
                  {metadataRows.map(item => (
                    <OverviewMetaItem key={item.label}>
                      <PreviewLabelText>{item.label}</PreviewLabelText>
                      <PreviewBody>{item.value}</PreviewBody>
                    </OverviewMetaItem>
                  ))}
                </OverviewMetaGrid>

                <OverviewDivider />

                <OverviewTagsSection>
                  <SectionEyebrow>Tags</SectionEyebrow>

                  {(tags?.length ?? 0) > 0 ? (
                    <TagsWrap>
                      {tags.map(tag => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </TagsWrap>
                  ) : (
                    <EmptyState>
                      Add category, discipline, collection, or style tags here.
                      This space is ready for real metadata without leaving a half-empty card.
                    </EmptyState>
                  )}
                </OverviewTagsSection>
              </>
            ) : (
              <Formik
                initialValues={defaults}
                validationSchema={schema}
                enableReinitialization={true}
                onSubmit={(values, handlers) => {
                  handleSubmit(values, handlers.setSubmitting);
                }}
              >
                {form => (
                  <Fragment>
                    {setRef(form)}

                    <OverviewHeader>
                      <OverviewHeaderTitle>Edit metadata</OverviewHeaderTitle>
                    </OverviewHeader>

                    <OverviewTagsSection>
                      <SectionEyebrow>Name</SectionEyebrow>
                      <Field type='text' name='contentName'>
                        {data => (
                          <FovInput
                            value={data.field.value}
                            disabled={modelsLoading}
                            placeholder={modelsLoading
                              ? 'Model data loading...'
                              : fileName || 'Enter a new name'}
                            {...data.field}
                            onChange={e => form.setFieldValue(data.field.name, e.target.value)}
                          />
                        )}
                      </Field>
                      <FovFieldError name='contentName' />
                    </OverviewTagsSection>

                    <OverviewDivider />

                    <OverviewTagsSection>
                      <SectionEyebrow>Category</SectionEyebrow>
                      <Field type='select' name='categoryName'>
                        {data => (
                          <FovSelect
                            value={data.field.value}
                            placeholder={data.field.value}
                            disabled={_.isEmpty(rpcCategories)}
                            onChange={(e, newVal) => form.setFieldValue(data.field.name, newVal)}
                          >
                            {categories}
                          </FovSelect>
                        )}
                      </Field>
                      <FovFieldError name='categoryName' />
                    </OverviewTagsSection>

                    <OverviewDivider />

                    <OverviewTagsSection>
                      <SectionEyebrow>Tags</SectionEyebrow>
                      <TagsContainer
                        theme={theme}
                        $selectedTheme={userDataStore.userPreferences.theme}
                      >
                        <Field type='input' name='tags'>
                          {data => (
                            <Tags
                              values={_.isEmpty(data.field.value)
                                ? []
                                : data.field.value.split(',')}
                              onChange={(e, nextTags) => {
                                form.setFieldValue(data.field.name, nextTags);
                              }}
                            />
                          )}
                        </Field>
                      </TagsContainer>
                      <FovFieldError name='tags' />
                    </OverviewTagsSection>

                    <OverviewDivider />

                    <ActionsInline>
                      <ActionButton
                        type="button"
                        onClick={() => {
                          form.resetForm();
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </ActionButton>

                      <FovButton
                        type='submit'
                        disabled={!statistics?.triangles || modelsLoading || form.isSubmitting}
                        onClick={form.handleSubmit}
                      >
                        {form.isSubmitting
                          ? <IndeterminateProgress />
                          : 'Save metadata'}
                      </FovButton>
                    </ActionsInline>
                  </Fragment>
                )}
              </Formik>
            )}
          </OverviewCard>
        </TabPanel>
      )}

      {activeTab === 'metadata' && (
        <TabPanel>
          <PanelGrid>
            <InfoCard>
              <InfoTitle>Technical Statistics</InfoTitle>
              {normalizedStats.length ? (
                <StatsGrid>
                  {normalizedStats.map(item => (
                    <StatCard key={item.label}>
                      <StatLabel>{item.label}</StatLabel>
                      <StatValue>{formatNumber(item.value)}</StatValue>
                    </StatCard>
                  ))}
                </StatsGrid>
              ) : (
                <EmptyState>No technical statistics are currently available for this preview.</EmptyState>
              )}
            </InfoCard>

            <InfoCard>
              <InfoTitle>Downloadable Formats</InfoTitle>

              {availableFormats.length ? (
                <TagsWrap>
                  {availableFormats.map(format => (
                    <FormatTag
                      key={format}
                      onClick={() => onDownload?.(format)}
                    >
                      {format}
                    </FormatTag>
                  ))}
                </TagsWrap>
              ) : (
                <EmptyState>No downloadable formats are currently marked as available.</EmptyState>
              )}
            </InfoCard>
          </PanelGrid>
        </TabPanel>
      )}
    </TabsCard>
  );
};

export default PreviewTabsCard;
